/** @typedef {import('./user.js').UserDocData} UserDocData */
import User from './user.js';

/**
 * @typedef {Object} AdminSchema
 * @property {boolean} allowLiveChanges - Allow admin to make live changes.
 * @property {boolean} canMoveTraveler - Allow admin to move travelers.
 * @property {boolean} canEditOrder - Allow admin to edit orders.
 *
 * @typedef {UserDocData & AdminSchema} AdminDocData
*/

/**
 * Class representing an administrator.
 * @class
 * @extends User
 */
export default class Admin extends User {
  /** @type {AdminDocData['allowLiveChanges']} */ allowLiveChanges;
  /** @type {AdminDocData['canMoveTraveler']} */ canMoveTraveler;
  /** @type {AdminDocData['canEditOrder']} */ canEditOrder;

  /**
   * Create an administrator.
   * @param {UserDocData} param0 - An {@link UserDocData} object.
   */
  constructor({
    allowLiveChanges, canMoveTraveler, canEditOrder, ...props
  }) {
    super({
      ...props,
    });
    this.allowLiveChanges = allowLiveChanges || false;
    this.canMoveTraveler = canMoveTraveler || false;
    this.canEditOrder = canEditOrder || false;
  }

  /**
   * Converts the {@link Admin} instance to a Firestore data object.
   * @returns {Omit<UserDocData, 'id'>} {@link AdminDocData} (without `id`).
   */
  toFirestore({ serverTimestamp }) {
    const baseDoc = super.toFirestore({ serverTimestamp });
    return {
      ...baseDoc,
      allowLiveChanges: this.allowLiveChanges,
      canMoveTraveler: this.canMoveTraveler,
      canEditOrder: this.canEditOrder,
    };
  }

  /**
   * Creates an {@link Admin} instance from Firestore data.
   * @param {any} docData - Raw Firestore document.
   * @returns An {@link Admin} instance.
   */
  static fromFirestore(docData) {
    return new Admin(docData);
  }
}
